// CTH V6.0 — PWA runtime: service-worker registration, safe update flow and
// install-prompt capture. Never stores tokens; never caches API data (see sw.js).

let deferredPrompt = null;
const listeners = new Set();

const notifyListeners = () => listeners.forEach((fn) => { try { fn(); } catch { /* noop */ } });

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // non-intrusive: we surface install from the menu only
    deferredPrompt = e;
    notifyListeners();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notifyListeners();
  });
}

export const pwa = {
  canPrompt: () => Boolean(deferredPrompt),
  async prompt() {
    if (!deferredPrompt) return null;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    notifyListeners();
    return choice?.outcome || null;
  },
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

export const isStandalone = () => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
  } catch { return false; }
};

export const isIOS = () => {
  const ua = navigator.userAgent || '';
  return /iphone|ipad|ipod/i.test(ua)
    || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
};

export function registerSW() {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', async () => {
    try {
      let hadController = Boolean(navigator.serviceWorker.controller);
      const reg = await navigator.serviceWorker.register('/sw.js');
      const notifyUpdate = () => window.dispatchEvent(new CustomEvent('cth:sw-update', { detail: reg }));
      if (reg.waiting && navigator.serviceWorker.controller) notifyUpdate();
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) notifyUpdate();
        });
      });
      // recheck for a new deployment every 30 min and on tab focus
      setInterval(() => reg.update().catch(() => {}), 30 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') reg.update().catch(() => {});
      });
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // First install: clients.claim() also fires controllerchange — never
        // reload then (it would wipe a login form / in-progress input).
        if (!hadController) { hadController = true; return; }
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      });
    } catch { /* SW is progressive enhancement — never block the app */ }
  });
}
